import React, { useState, useRef, useEffect, useCallback } from "react";
import { Upload, Video, Link as LinkIcon, Camera, Square, CheckCircle2, Trash2, Circle } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

/**
 * VideoInput — reusable component providing 3 ways to attach a video to a culte:
 *   1. URL — YouTube, Facebook, or direct .mp4 / .m3u8
 *   2. Upload — file picker with drag-and-drop + progress bar
 *   3. Record — webcam & mic capture via MediaRecorder, then upload
 *
 * Props:
 *   value: { video_type, video_url }
 *   onChange(next): called with { video_type, video_url }
 */
export default function VideoInput({ value, onChange }) {
  const [tab, setTab] = useState(
    value?.video_type === "upload" || value?.video_type === "url" || !value?.video_type
      ? (value?.video_type === "upload" ? "upload" : "url")
      : "url"
  );

  const setValue = (patch) => onChange({ ...value, ...patch });

  return (
    <div className="rounded-xl border-2 border-slate-200 bg-slate-50 overflow-hidden">
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="w-full grid grid-cols-3 rounded-none bg-white h-auto p-0 border-b border-slate-200" data-testid="video-input-tabs">
          <TabsTrigger value="url" className="rounded-none py-3 data-[state=active]:bg-deepblue-900 data-[state=active]:text-white">
            <LinkIcon className="w-4 h-4 mr-2" /> URL
          </TabsTrigger>
          <TabsTrigger value="upload" className="rounded-none py-3 data-[state=active]:bg-deepblue-900 data-[state=active]:text-white">
            <Upload className="w-4 h-4 mr-2" /> Téléverser
          </TabsTrigger>
          <TabsTrigger value="record" className="rounded-none py-3 data-[state=active]:bg-deepblue-900 data-[state=active]:text-white">
            <Camera className="w-4 h-4 mr-2" /> Enregistrer
          </TabsTrigger>
        </TabsList>

        <TabsContent value="url" className="p-5 space-y-3">
          <div className="grid grid-cols-3 gap-2">
            <button type="button" onClick={() => setValue({ video_type: "youtube" })}
              className={`px-3 py-2 rounded-lg border text-xs font-semibold ${value?.video_type === "youtube" ? "bg-red-500 text-white border-red-500" : "bg-white border-slate-200"}`}>
              YouTube
            </button>
            <button type="button" onClick={() => setValue({ video_type: "facebook" })}
              className={`px-3 py-2 rounded-lg border text-xs font-semibold ${value?.video_type === "facebook" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-200"}`}>
              Facebook
            </button>
            <button type="button" onClick={() => setValue({ video_type: "url" })}
              className={`px-3 py-2 rounded-lg border text-xs font-semibold ${value?.video_type === "url" ? "bg-deepblue-900 text-white border-deepblue-900" : "bg-white border-slate-200"}`}>
              Autre URL
            </button>
          </div>
          <Input
            value={value?.video_url || ""}
            onChange={(e) => setValue({ video_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=…"
            data-testid="video-url-input"
          />
        </TabsContent>

        <TabsContent value="upload" className="p-5">
          <UploadDropzone
            onDone={(url) => setValue({ video_type: "upload", video_url: url })}
            currentUrl={value?.video_type === "upload" ? value?.video_url : null}
          />
        </TabsContent>

        <TabsContent value="record" className="p-5">
          <WebcamRecorder
            onDone={(url) => setValue({ video_type: "upload", video_url: url })}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

/* --------------------- Upload dropzone --------------------- */
function UploadDropzone({ onDone, currentUrl }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(!!currentUrl);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => { setDone(!!currentUrl); }, [currentUrl]);

  const uploadFile = async (file) => {
    if (!file) return;
    setUploading(true);
    setProgress(0);
    setDone(false);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      onDone(data.url);
      setDone(true);
      toast.success("Vidéo téléversée avec succès");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du téléversement");
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  return (
    <div>
      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        onClick={() => !uploading && inputRef.current?.click()}
        className={`cursor-pointer rounded-xl border-2 border-dashed p-8 text-center transition-colors ${
          dragging ? "border-gold bg-gold/10" : uploading ? "border-blue-300 bg-blue-50" : "border-slate-300 bg-white hover:border-deepblue-600 hover:bg-blue-50"
        }`}
        data-testid="upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept="video/*"
          hidden
          onChange={(e) => uploadFile(e.target.files?.[0])}
          data-testid="upload-file-input"
        />
        {done && !uploading ? (
          <div className="space-y-2">
            <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-500" />
            <div className="font-semibold text-deepblue-900">Vidéo téléversée ✓</div>
            <div className="text-xs text-slate-500">Cliquez pour remplacer</div>
          </div>
        ) : uploading ? (
          <div className="space-y-3">
            <Video className="w-12 h-12 mx-auto text-deepblue-600 animate-pulse" />
            <div className="font-semibold text-deepblue-900">Téléversement en cours…</div>
            <Progress value={progress} className="h-2" />
            <div className="text-sm text-slate-500">{progress}%</div>
          </div>
        ) : (
          <div className="space-y-2">
            <Upload className="w-12 h-12 mx-auto text-deepblue-600" />
            <div className="font-semibold text-deepblue-900">Cliquez ou glissez votre vidéo ici</div>
            <div className="text-xs text-slate-500">MP4, WebM, MOV — jusqu'à 500 Mo recommandé</div>
          </div>
        )}
      </div>
    </div>
  );
}

/* --------------------- Webcam recorder --------------------- */
function WebcamRecorder({ onDone }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const [status, setStatus] = useState("idle"); // idle | ready | recording | recorded | uploading
  const [elapsed, setElapsed] = useState(0);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [progress, setProgress] = useState(0);
  const [devices, setDevices] = useState({ cameras: [], mics: [] });
  const [selectedCam, setSelectedCam] = useState("");
  const [selectedMic, setSelectedMic] = useState("");

  const stopTracks = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  const listDevices = useCallback(async () => {
    try {
      const all = await navigator.mediaDevices.enumerateDevices();
      setDevices({
        cameras: all.filter((d) => d.kind === "videoinput"),
        mics: all.filter((d) => d.kind === "audioinput"),
      });
    } catch (e) { /* ignore */ }
  }, []);

  const startPreview = async () => {
    try {
      stopTracks();
      const constraints = {
        video: selectedCam ? { deviceId: { exact: selectedCam } } : true,
        audio: selectedMic ? { deviceId: { exact: selectedMic } } : true,
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      setStatus("ready");
      listDevices();
    } catch (e) {
      toast.error("Impossible d'accéder à la caméra/micro. Vérifiez les autorisations.");
    }
  };

  const startRecording = () => {
    if (!streamRef.current) return;
    chunksRef.current = [];
    const mimeType = MediaRecorder.isTypeSupported("video/webm;codecs=vp9,opus")
      ? "video/webm;codecs=vp9,opus"
      : "video/webm";
    const rec = new MediaRecorder(streamRef.current, { mimeType });
    rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
    rec.onstop = () => {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      setPreviewUrl(url);
      setStatus("recorded");
      clearInterval(timerRef.current);
    };
    rec.start(1000);
    recorderRef.current = rec;
    setElapsed(0);
    setStatus("recording");
    timerRef.current = setInterval(() => setElapsed((s) => s + 1), 1000);
  };

  const stopRecording = () => {
    if (recorderRef.current && recorderRef.current.state !== "inactive") {
      recorderRef.current.stop();
    }
    stopTracks();
  };

  const uploadRecording = async () => {
    if (chunksRef.current.length === 0) return;
    setStatus("uploading");
    setProgress(0);
    try {
      const blob = new Blob(chunksRef.current, { type: "video/webm" });
      const file = new File([blob], `enregistrement-${Date.now()}.webm`, { type: "video/webm" });
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (e) => {
          if (e.total) setProgress(Math.round((e.loaded * 100) / e.total));
        },
      });
      onDone(data.url);
      toast.success("Enregistrement téléversé avec succès");
      setStatus("recorded");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du téléversement");
      setStatus("recorded");
    }
  };

  const reset = () => {
    chunksRef.current = [];
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setElapsed(0);
    setStatus("idle");
    stopTracks();
  };

  useEffect(() => () => { stopTracks(); clearInterval(timerRef.current); if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const fmt = (s) => `${Math.floor(s / 60).toString().padStart(2, "0")}:${(s % 60).toString().padStart(2, "0")}`;

  return (
    <div className="space-y-4" data-testid="webcam-recorder">
      <div className="relative rounded-xl overflow-hidden bg-black aspect-video">
        {status === "recorded" && previewUrl ? (
          <video src={previewUrl} controls className="w-full h-full" />
        ) : (
          <video ref={videoRef} muted playsInline className="w-full h-full object-cover" />
        )}
        {status === "recording" && (
          <div className="absolute top-3 left-3 flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-600 text-white text-xs font-bold">
            <span className="live-dot w-2 h-2 rounded-full bg-white" />
            REC · {fmt(elapsed)}
          </div>
        )}
      </div>

      {status === "idle" && (
        <div className="space-y-3">
          {devices.cameras.length > 0 && (
            <div className="grid grid-cols-2 gap-3">
              <Select value={selectedCam} onValueChange={setSelectedCam}>
                <SelectTrigger><SelectValue placeholder="Caméra (par défaut)" /></SelectTrigger>
                <SelectContent>
                  {devices.cameras.map((c) => <SelectItem key={c.deviceId} value={c.deviceId}>{c.label || "Caméra"}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={selectedMic} onValueChange={setSelectedMic}>
                <SelectTrigger><SelectValue placeholder="Micro (par défaut)" /></SelectTrigger>
                <SelectContent>
                  {devices.mics.map((c) => <SelectItem key={c.deviceId} value={c.deviceId}>{c.label || "Micro"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          <Button onClick={startPreview} className="w-full bg-deepblue-900 hover:bg-deepblue-800 text-white" data-testid="record-preview-btn">
            <Camera className="w-4 h-4 mr-2" /> Activer la caméra
          </Button>
        </div>
      )}

      {status === "ready" && (
        <Button onClick={startRecording} className="w-full bg-red-600 hover:bg-red-700 text-white" data-testid="record-start-btn">
          <Circle className="w-4 h-4 mr-2 fill-white" /> Démarrer l'enregistrement
        </Button>
      )}

      {status === "recording" && (
        <Button onClick={stopRecording} className="w-full bg-slate-800 hover:bg-slate-900 text-white" data-testid="record-stop-btn">
          <Square className="w-4 h-4 mr-2 fill-white" /> Arrêter l'enregistrement
        </Button>
      )}

      {status === "recorded" && (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Button onClick={reset} variant="outline" data-testid="record-reset-btn">
              <Trash2 className="w-4 h-4 mr-2" /> Recommencer
            </Button>
            <Button onClick={uploadRecording} className="bg-gold hover:bg-gold-hover text-white" data-testid="record-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Téléverser
            </Button>
          </div>
        </div>
      )}

      {status === "uploading" && (
        <div className="space-y-2">
          <Progress value={progress} className="h-2" />
          <div className="text-sm text-slate-500 text-center">Téléversement… {progress}%</div>
        </div>
      )}
    </div>
  );
}
